/**
 * The conflict model: what happens when two devices changed the same thing.
 *
 * THE RULE THIS FILE ENFORCES
 * ---------------------------
 * Blind last-write-wins is used NOWHERE, and the reason is specific rather than
 * general: a "last write wins" rule applied to a stock ledger DELETES a movement
 * that actually happened. The ledger is append-only on purpose — a balance that
 * can be silently edited is a balance nobody can reconcile — and a sync layer that
 * helpfully made the newest version win would quietly undo real business events,
 * with no report ever showing it.
 *
 * So conflicts are classified by what the RECORD MEANS, and the classification
 * is made here, in domain, where a provider cannot reach it.
 */
import type { SyncChange } from './changeModel';

/** What a record is, for the purpose of deciding how two versions may merge. */
export type RecordClass =
  /** Customers, suppliers, items. Editable; two edits can sometimes be merged. */
  | 'MASTER'
  /** Invoices and their lines. Once ISSUED, never silently rewritten. */
  | 'FINALIZED_DOCUMENT'
  /** Payments and their allocations. Never merged, never averaged. */
  | 'PAYMENT'
  /** The stock ledger. Append-only, always. */
  | 'STOCK_LEDGER'
  /** Order, delivery and other in-progress documents. */
  | 'DRAFT_DOCUMENT';

export type ConflictResolution =
  /** The incoming change is not newer than what we hold. Ignore it. */
  | 'KEEP_LOCAL'
  /** The incoming change is newer and safe to take. */
  | 'TAKE_REMOTE'
  /**
   * Merge field by field. Only for MASTER records, and only when the two
   * versions touched DISJOINT fields. Two edits to the same field is a
   * conflict, not a merge — silently preferring one is what this model exists
   * to prevent.
   */
  | 'MERGE'
  /**
   * A human must choose. Written to the conflict log and shown in the UI.
   *
   * This is the honest answer, and it is used whenever an automatic resolution
   * would fabricate a business event. A user told "these two invoices disagree,
   * pick one" can recover. A user whose ledger was silently rewritten cannot.
   */
  | 'REQUIRES_USER';

/** Why the model chose what it chose. Carried into the log for support. */
export type ConflictReason =
  /** Same version: the same change arriving twice. Not a conflict at all. */
  | 'DUPLICATE'
  /** The remote version is behind what we already hold. */
  | 'STALE_REMOTE'
  /** A plain edit, newer than ours, on a record where that is safe. */
  | 'REMOTE_NEWER'
  /** Both changed, both safe to merge, on different fields. */
  | 'DISJOINT_FIELDS'
  /** Both changed the same field. */
  | 'SAME_FIELD'
  /** A finalized record whose two versions disagree. */
  | 'FINALIZED_DIVERGENCE'
  /** A stock movement would be rewritten. Never permitted. */
  | 'LEDGER_APPEND_ONLY';

export interface ConflictInput {
  readonly entityType: string;
  readonly recordClass: RecordClass;
  readonly localVersion: number;
  readonly remoteVersion: number;
  /**
   * True when the incoming change is PROVABLY a different mutation from the one
   * that produced the local row, at the SAME version.
   *
   * This is the one input a version cannot express. Equal versions normally mean
   * "the same change arrived twice" — the idempotency path — but two devices
   * editing one row from the same base both reach version 2, and then the equal
   * numbers are a COLLISION, not a repeat. Left false, the equal case is treated
   * as the retry it usually is; set true, the model is asked the concurrency
   * question instead, and the class rules below answer it.
   */
  readonly concurrent?: boolean;
  /**
   * The fields each side changed, when known.
   *
   * Optional because a provider may not be able to tell us, and the model must
   * still answer. When absent, the answer is the conservative one.
   */
  readonly localChangedFields?: readonly string[];
  readonly remoteChangedFields?: readonly string[];
}

export interface ConflictDecision {
  readonly resolution: ConflictResolution;
  readonly reason: ConflictReason;
  /** The fields to take from the remote, when merging. */
  readonly mergeFields?: readonly string[];
}

/** Master records and in-progress documents may be merged. */
const MERGEABLE: readonly RecordClass[] = ['MASTER', 'DRAFT_DOCUMENT'];

export function resolveConflict(input: ConflictInput): ConflictDecision {
  // An OLDER version is a stale replay: the row already moved past it, and taking
  // it would undo work that is newer. Never a conflict, and never a rewrite.
  if (input.remoteVersion < input.localVersion) {
    return { resolution: 'KEEP_LOCAL', reason: 'STALE_REMOTE' };
  }

  // An EQUAL version is USUALLY the same change arriving twice — a retry, which
  // is the idempotency path and not a conflict. It is only usually: two devices
  // editing one row from the same base both compute version 2, so an equal version
  // carrying a DIFFERENT mutation id is two live edits colliding, and it falls
  // through to the class rules below. Those rules then decide — and for everything
  // that is not provably mergeable, the answer is a person.
  if (input.remoteVersion === input.localVersion && !input.concurrent) {
    return { resolution: 'KEEP_LOCAL', reason: 'DUPLICATE' };
  }

  // ── The stock ledger. Append-only, without exception. ────────────────────
  //
  // A movement is a FACT ("two widgets left the shop on Tuesday"), not a mutable
  // field. Two devices cannot both be right about one, and no merge produces a
  // true answer. A reversal is a NEW movement, which is how the application
  // already models it (ADR 0008 §4).
  if (input.recordClass === 'STOCK_LEDGER') {
    return { resolution: 'REQUIRES_USER', reason: 'LEDGER_APPEND_ONLY' };
  }

  // ── Payments. Never merged, never averaged, never auto-resolved. ─────────
  //
  // Two devices recording the same receipt is a real reconciliation question,
  // not a technical one. Answering it by picking one could leave a customer
  // short-paid with no trace.
  if (input.recordClass === 'PAYMENT') {
    return { resolution: 'REQUIRES_USER', reason: 'SAME_FIELD' };
  }

  // ── A finalized document that has DIVERGED. ─────────────────────────────
  //
  // Note the order: an incoming change that is merely NEWER and touches nothing
  // we changed is still safe to take. What is refused is a change that would
  // rewrite a document we have already issued. A GST invoice is a legal document;
  // the correction is a credit note, which exists precisely because it cannot be
  // edited (ADR 0008).
  if (input.recordClass === 'FINALIZED_DOCUMENT') {
    const localFields = input.localChangedFields;
    const remoteFields = input.remoteChangedFields;
    // An EMPTY local set means this device changed nothing, so there is no
    // divergence to protect — `[].every(...)` is true, which is the correct
    // answer. Requiring a non-empty list here would instead raise a conflict
    // for the safest possible case: a device that only ever read the document.
    const disjoint =
      Array.isArray(localFields) &&
      Array.isArray(remoteFields) &&
      localFields.every((f) => !remoteFields.includes(f));
    if (disjoint) {
      return { resolution: 'TAKE_REMOTE', reason: 'REMOTE_NEWER' };
    }
    return { resolution: 'REQUIRES_USER', reason: 'FINALIZED_DIVERGENCE' };
  }

  // ── Master and draft records. Merge only when provably disjoint. ─────────
  const localFields = input.localChangedFields;
  const remoteFields = input.remoteChangedFields;
  if (
    MERGEABLE.includes(input.recordClass) &&
    Array.isArray(localFields) &&
    Array.isArray(remoteFields)
  ) {
    const overlapping = localFields.filter((f) => remoteFields.includes(f));
    if (overlapping.length === 0) {
      return { resolution: 'MERGE', reason: 'DISJOINT_FIELDS', mergeFields: [...remoteFields] };
    }
    // Same field, two values. Prefer the newer edit — this is the ONE place
    // last-write-wins is acceptable, because both sides are drafts of editable
    // master data and the loser is a human's own earlier typo.
    //
    // Unless the two are CONCURRENT. Then there is no "newer" to prefer: both
    // edits started from the same base, and preferring one by arrival order is
    // precisely the last-write-wins this model refuses. A person decides.
    if (input.concurrent) {
      return { resolution: 'REQUIRES_USER', reason: 'SAME_FIELD' };
    }
    return { resolution: 'TAKE_REMOTE', reason: 'SAME_FIELD' };
  }

  // No field information. For a safe class take the newer; for anything else,
  // do not guess.
  if (MERGEABLE.includes(input.recordClass)) {
    // EXCEPT when there is no "newer" to take. A concurrent change with no field
    // information cannot be merged (a merge needs to know WHICH fields each side
    // touched) and must not be taken (it is not newer — taking it would be
    // last-arrival-wins). It becomes a question for a person, with both edits
    // still intact on their own devices.
    if (input.concurrent) {
      return { resolution: 'REQUIRES_USER', reason: 'SAME_FIELD' };
    }
    return { resolution: 'TAKE_REMOTE', reason: 'REMOTE_NEWER' };
  }
  return { resolution: 'REQUIRES_USER', reason: 'SAME_FIELD' };
}

/**
 * A conflict a human must resolve.
 *
 * Persisted rather than merely logged: a conflict that exists only in a log line
 * is a conflict the user will never see, and "someone else changed this while you
 * were offline" must be actionable or it is merely alarming.
 */
export interface SyncConflict {
  readonly conflictId: string;
  readonly businessId: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly localDeviceId: string;
  readonly remoteDeviceId: string;
  readonly reason: ConflictReason;
  readonly localVersion: number;
  readonly remoteVersion: number;
  readonly detectedAt: string;
  /** What the user should do, in words. An error code is not advice. */
  readonly guidance: string;
  /** True once a person has dealt with it. */
  readonly resolved: boolean;
  /** Which way they chose, for the audit trail. */
  readonly resolution?: 'LOCAL' | 'REMOTE';
  /** When it was resolved, so the audit trail has a time and not only a flag. */
  readonly resolvedAt?: string;
  /**
   * The device that acknowledged it.
   *
   * Always known, and deliberately kept separate from the user: a device with no
   * signed-in account can still acknowledge a conflict, and inventing a person
   * who did it would make the audit record a fiction.
   */
  readonly resolvedByDeviceId?: string;
  /** The signed-in account that acknowledged it, absent when nobody was signed in. */
  readonly resolvedByUserId?: string;
  /**
   * The local version that was actually kept.
   *
   * Equal to `localVersion` in the ordinary case. It differs only when the record
   * moved on after detection and a person confirmed the CURRENT version instead —
   * `localVersion` still says what was true at detection, because that is history
   * and rewriting it would erase why the conflict exists at all.
   */
  readonly resolvedLocalVersion?: number;
  /**
   * THE REFUSED CHANGE ITSELF.
   *
   * The apply batch advances its cursor after the batch finishes, so a change
   * this model refuses is not downloaded again — the provider's position has
   * already moved past it. Without a copy of the change, the conflict row is a
   * warning that can never be acted on: the user is told two versions disagree
   * but only one of them still exists anywhere the device can reach.
   *
   * Kept in full (see `conflictRecovery.ts` for the measured size cap) so that a
   * future `USE_REMOTE` can re-apply exactly the change that was refused, and so
   * that a decision today can be validated against the same identity tomorrow.
   */
  readonly pendingChange?: SyncChange;
  /** Stored size of `pendingChange`, in JSON bytes. Recorded, not recomputed. */
  readonly pendingChangeBytes?: number;
  /**
   * Set when the change was too large to keep in full.
   *
   * `pendingChange` is then kept WITHOUT its payload: the identity and version
   * survive (enough to acknowledge), and the absence is recorded rather than
   * guessed at later, so a future remote-apply can refuse instead of writing a
   * half-change.
   */
  readonly pendingChangeOmitted?: 'TOO_LARGE';
}

/** Classify an entity type into the class the rules above use. */
export function classOfEntity(entityType: string): RecordClass {
  if (entityType === 'stockTransaction') return 'STOCK_LEDGER';
  if (entityType === 'payment' || entityType === 'paymentAllocation') return 'PAYMENT';
  if (entityType === 'document' || entityType === 'documentLine') return 'FINALIZED_DOCUMENT';
  return 'MASTER';
}

/** Words a person can act on. */
export function guidanceFor(reason: ConflictReason): string {
  switch (reason) {
    case 'LEDGER_APPEND_ONLY':
      return 'Two devices recorded different stock movements. Stock is append-only, so nothing was changed. Check both devices, then record a correction if one is wrong.';
    case 'FINALIZED_DIVERGENCE':
      return 'An issued document was changed on another device after it was issued here. Issued documents are never overwritten. Compare the two, and use a credit note or cancellation to correct it.';
    case 'SAME_FIELD':
      return 'Two devices changed this record differently. Nothing was overwritten. Choose which version is correct.';
    case 'STALE_REMOTE':
    case 'DUPLICATE':
      return 'This change was already applied. Nothing to do.';
    case 'REMOTE_NEWER':
      return 'A newer version of this record was taken from the other device.';
    case 'DISJOINT_FIELDS':
      return 'Both devices edited different fields of this record, and the edits were combined.';
  }
}

